import { inject, Injectable } from "@angular/core";
import { catchError, EMPTY, interval, Subscription, take } from "rxjs";

import { AuthService } from "@app/core/services/auth.service";
import { AccountService } from "@app/shared/services/account.service";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RENEW_BEFORE_MS = 10 * 60 * 1000;

/**
 * ROR-6097: keeps a working session alive while the user is filling in a long form.
 *
 * The refresh token has a sliding lifetime, but it only slides when a request is actually sent — and
 * a wizard step sends none while it is being filled in. Half an hour of typing was therefore enough
 * to end the session and lose the step. This sends one request shortly before the window would lapse:
 * the access token is expired by then, so it goes through UnauthorizedInterceptor's normal refresh
 * (cross-tab lock included) and the window slides. Nothing else here knows how to refresh a token.
 */
@Injectable({ providedIn: "root" })
export class SessionKeepAliveService {
  private readonly authService = inject(AuthService);
  private readonly accountService = inject(AccountService);

  private timerSubscription: Subscription | null = null;

  start(): void {
    if (this.timerSubscription) {
      return;
    }

    this.timerSubscription = interval(CHECK_INTERVAL_MS).subscribe(() => this.renewIfExpiringSoon());
  }

  stop(): void {
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = null;
  }

  // Signed out, or the session has plenty of time left: nothing to do. Errors are swallowed because a
  // failed keep-alive is not the user's problem — the next real request reports it, and a genuinely
  // dead session is ended by UnauthorizedInterceptor.
  private renewIfExpiringSoon(): void {
    const expiresAt = this.readRefreshExpiry();

    if (expiresAt === null || expiresAt - Date.now() > RENEW_BEFORE_MS) {
      return;
    }

    this.accountService
      .getUserDetails()
      .pipe(take(1), catchError(() => EMPTY))
      .subscribe();
  }

  private readRefreshExpiry(): number | null {
    if (!this.authService.getAccessToken()) {
      return null;
    }

    const storedExpiry = this.authService.getRefreshDataEx();
    const expiresAt = storedExpiry ? new Date(storedExpiry).getTime() : Number.NaN;

    return Number.isNaN(expiresAt) ? null : expiresAt;
  }
}
